import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { io, Socket } from 'socket.io-client';
import { Conversation, Message, PaginatedConversations, PaginatedMessages, LearningHighlight, ChatAnalysisStats, LearningInsightsPayload } from '../types/messaging';
import { apiClient } from '../services/apiClient';

interface MessagingState {
    conversations: Conversation[];
    currentConversation: Conversation | null;
    messages: Message[];
    unreadCount: number;
    loading: boolean;
    sendingMessage: boolean;
    error: string | null;
    hasMoreMessages: boolean;
    hasMoreConversations: boolean;
    page: number;
    messagePage: number;
    socket: Socket | null;
    isConnected: boolean;
    typingUsers: Map<string, string[]>; // conversationId -> userId[]
    learningHighlights: LearningHighlight[];
    chatStats: ChatAnalysisStats | null;
}

interface MessagingActions {
    connect: (token?: string) => void;
    disconnect: () => void;
    fetchConversations: (page?: number) => Promise<void>;
    fetchConversation: (conversationId: string) => Promise<void>;
    fetchMessages: (conversationId: string, page?: number) => Promise<void>;
    sendMessage: (conversationId: string, content: string) => Promise<Message | null>;
    startConversation: (participantId: string, initialMessage?: string) => Promise<Conversation | null>;
    markAsRead: (conversationId: string) => Promise<void>;
    fetchUnreadCount: () => Promise<void>;
    setCurrentConversation: (conversation: Conversation | null) => void;
    startTyping: (conversationId: string) => void;
    stopTyping: (conversationId: string) => void;
    joinConversation: (conversationId: string) => void;
    leaveConversation: (conversationId: string) => void;
    clearHighlights: () => void;
    /**
     * Optimistic insert. Used by voice mode to show the user's bubble as
     * "transcribing…" the instant they release the PTT button — before the
     * ASR round-trip completes. The server response then patches the same
     * row with the real id + transcript via `updateLocalMessage`.
     */
    addLocalMessage: (message: Message) => void;
    /**
     * Patch (or replace) a message already in the local store. Match by the
     * current id; the patch may include a new id.
     */
    updateLocalMessage: (currentId: string, patch: Partial<Message>) => void;
    /** Drop a message from the local store (e.g. ASR rejected the audio). */
    removeLocalMessage: (id: string) => void;
}

type MessagingStore = MessagingState & MessagingActions;

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// ---- Typing indicator auto-clear safety net --------------------------------
// Holds one timer per (conversationId, userId). If 15 seconds elapse without a
// fresh typing or typing_stopped event, we proactively clear the stale state.
const TYPING_AUTO_CLEAR_MS = 15000;
const typingAutoClearTimers = new Map<string, ReturnType<typeof setTimeout>>();

function typingTimerKey(conversationId: string, userId: string): string {
    return `${conversationId}::${userId}`;
}

function scheduleTypingAutoClear(
    conversationId: string,
    userId: string,
    setState: (
        fn: (state: MessagingState) => Partial<MessagingState>,
    ) => void,
): void {
    const key = typingTimerKey(conversationId, userId);
    const existing = typingAutoClearTimers.get(key);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
        typingAutoClearTimers.delete(key);
        setState((state) => {
            const newTypingUsers = new Map(state.typingUsers);
            const users = newTypingUsers.get(conversationId) || [];
            if (!users.includes(userId)) return {};
            newTypingUsers.set(
                conversationId,
                users.filter((u) => u !== userId),
            );
            return { typingUsers: newTypingUsers };
        });
    }, TYPING_AUTO_CLEAR_MS);
    typingAutoClearTimers.set(key, timer);
}

function cancelTypingAutoClear(conversationId: string, userId: string): void {
    const key = typingTimerKey(conversationId, userId);
    const existing = typingAutoClearTimers.get(key);
    if (existing) {
        clearTimeout(existing);
        typingAutoClearTimers.delete(key);
    }
}

function clearAllTypingForConversation(conversationId: string): void {
    for (const key of Array.from(typingAutoClearTimers.keys())) {
        if (key.startsWith(`${conversationId}::`)) {
            const t = typingAutoClearTimers.get(key);
            if (t) clearTimeout(t);
            typingAutoClearTimers.delete(key);
        }
    }
}

export const useMessagingStore = create<MessagingStore>()(
    devtools(
        (set, get) => ({
            // State
            conversations: [],
            currentConversation: null,
            messages: [],
            unreadCount: 0,
            loading: false,
            sendingMessage: false,
            error: null,
            hasMoreMessages: true,
            hasMoreConversations: true,
            page: 1,
            messagePage: 1,
            socket: null,
            isConnected: false,
            typingUsers: new Map(),
            learningHighlights: [],
            chatStats: null,

            // Actions
            connect: (token?: string) => {
                const { socket } = get();
                if (socket?.connected) return;

                // Get token from parameter or localStorage
                const authToken = token || localStorage.getItem('accessToken');
                if (!authToken) {
                    console.warn('No auth token available for WebSocket connection');
                    return;
                }

                const newSocket = io(`${API_BASE_URL}/messages`, {
                    auth: { token: authToken },
                    transports: ['websocket'],
                });

                newSocket.on('connect', () => {
                    set({ isConnected: true });
                });

                newSocket.on('disconnect', () => {
                    set({ isConnected: false });
                });

                newSocket.on('message', (message: Message) => {
                    const { currentConversation } = get();
                    if (message.conversationId === currentConversation?.id) {
                        set((state) => ({
                            messages: [...state.messages, message],
                        }));
                    }
                    // Update conversation list
                    set((state) => ({
                        conversations: state.conversations.map((c) =>
                            c.id === message.conversationId
                                ? { ...c, lastMessage: message, lastMessageAt: message.createdAt }
                                : c
                        ),
                    }));
                });

                newSocket.on('new_message', (_data: { conversationId: string; senderId: string }) => {
                    get().fetchUnreadCount();
                });

                // Patch event for an existing message — currently emitted by
                // the AI Chat Buddy "regenerate TTS" path. Only mutates fields
                // that the server actually sent (audioUrl, audioDurationMs,
                // metadata) so we don't accidentally clobber the original
                // content / sender info.
                newSocket.on('message_updated', (patch: {
                    id: string;
                    conversationId: string;
                    audioUrl?: string | null;
                    audioDurationMs?: number | null;
                    metadata?: Record<string, unknown> | null;
                }) => {
                    const { currentConversation } = get();
                    if (patch.conversationId !== currentConversation?.id) return;
                    set((state) => ({
                        messages: state.messages.map((m) => {
                            if (m.id !== patch.id) return m;
                            return {
                                ...m,
                                ...(patch.audioUrl !== undefined ? { audioUrl: patch.audioUrl } : {}),
                                ...(patch.audioDurationMs !== undefined ? { audioDurationMs: patch.audioDurationMs } : {}),
                                ...(patch.metadata !== undefined && patch.metadata !== null
                                    ? { metadata: { ...(m.metadata ?? {}), ...patch.metadata } }
                                    : {}),
                            };
                        }),
                    }));
                });

                newSocket.on('unread_count', (data: { count: number }) => {
                    set({ unreadCount: data.count });
                });

                newSocket.on('typing', (data: { conversationId: string; userId: string }) => {
                    set((state) => {
                        const newTypingUsers = new Map(state.typingUsers);
                        const users = newTypingUsers.get(data.conversationId) || [];
                        if (!users.includes(data.userId)) {
                            newTypingUsers.set(data.conversationId, [...users, data.userId]);
                        }
                        return { typingUsers: newTypingUsers };
                    });
                    // Safety net: auto-clear this user's typing state after 15s
                    // in case the stop_typing event never arrives (e.g. user
                    // switched away from the conversation room before the bot
                    // finished replying).
                    scheduleTypingAutoClear(data.conversationId, data.userId, set);
                });

                newSocket.on('typing_stopped', (data: { conversationId: string; userId: string }) => {
                    cancelTypingAutoClear(data.conversationId, data.userId);
                    set((state) => {
                        const newTypingUsers = new Map(state.typingUsers);
                        const users = newTypingUsers.get(data.conversationId) || [];
                        newTypingUsers.set(
                            data.conversationId,
                            users.filter((u) => u !== data.userId)
                        );
                        return { typingUsers: newTypingUsers };
                    });
                });

                newSocket.on('messages_read', (data: { conversationId: string; readBy: string }) => {
                    // Update read status in messages
                    const { currentConversation } = get();
                    if (data.conversationId === currentConversation?.id) {
                        set((state) => ({
                            messages: state.messages.map((m) =>
                                m.senderId !== data.readBy && !m.isRead
                                    ? { ...m, isRead: true, readAt: new Date().toISOString() }
                                    : m
                            ),
                        }));
                    }
                });

                newSocket.on('learning_insights', (data: LearningInsightsPayload) => {
                    const { currentConversation } = get();
                    if (data.conversationId === currentConversation?.id) {
                        set({
                            learningHighlights: data.highlights,
                            chatStats: data.stats,
                        });
                    }
                });

                newSocket.on('connect_error', (error) => {
                    console.error('Messaging socket error:', error);
                    set({ error: 'Failed to connect to messaging' });
                });

                set({ socket: newSocket });
            },

            disconnect: () => {
                const { socket } = get();
                if (socket) {
                    socket.disconnect();
                    set({ socket: null, isConnected: false });
                }
            },

            fetchConversations: async (page = 1) => {
                set({ loading: true, error: null });
                try {
                    const response = await apiClient.get<PaginatedConversations>(
                        `/conversations`,
                        { params: { page, limit: 20 } }
                    );

                    const { items, totalPages } = response.data;

                    if (page === 1) {
                        set({
                            conversations: items,
                            page,
                            hasMoreConversations: page < totalPages,
                            loading: false,
                        });
                    } else {
                        set((state) => ({
                            conversations: [...state.conversations, ...items],
                            page,
                            hasMoreConversations: page < totalPages,
                            loading: false,
                        }));
                    }
                } catch (error: any) {
                    set({
                        loading: false,
                        error: error.response?.data?.message || 'Failed to fetch conversations',
                    });
                }
            },

            fetchConversation: async (conversationId: string) => {
                set({ loading: true, error: null });
                try {
                    const response = await apiClient.get<Conversation>(
                        `/conversations/${conversationId}`
                    );
                    set({ currentConversation: response.data, loading: false });
                } catch (error: any) {
                    set({
                        loading: false,
                        error: error.response?.data?.message || 'Failed to fetch conversation',
                    });
                }
            },

            fetchMessages: async (conversationId: string, page = 1) => {
                set({ loading: true, error: null });
                try {
                    const response = await apiClient.get<PaginatedMessages>(
                        `/conversations/${conversationId}/messages`,
                        { params: { page, limit: 50 } }
                    );

                    const { items, hasMore } = response.data;

                    if (page === 1) {
                        set({
                            messages: items,
                            messagePage: page,
                            hasMoreMessages: hasMore,
                            loading: false,
                        });
                    } else {
                        set((state) => ({
                            messages: [...items, ...state.messages],
                            messagePage: page,
                            hasMoreMessages: hasMore,
                            loading: false,
                        }));
                    }
                } catch (error: any) {
                    set({
                        loading: false,
                        error: error.response?.data?.message || 'Failed to fetch messages',
                    });
                }
            },

            sendMessage: async (conversationId: string, content: string) => {
                set({ sendingMessage: true, error: null });
                try {
                    const response = await apiClient.post<Message>(
                        `/conversations/${conversationId}/messages`,
                        { content }
                    );

                    const message = response.data;

                    set((state) => ({
                        messages: [...state.messages, message],
                        sendingMessage: false,
                    }));

                    // Update conversation in list
                    set((state) => ({
                        conversations: state.conversations.map((c) =>
                            c.id === conversationId
                                ? { ...c, lastMessage: message, lastMessageAt: message.createdAt }
                                : c
                        ),
                    }));

                    return message;
                } catch (error: any) {
                    const errorMessage = error.response?.data?.message || 'Failed to send message';
                    set({ sendingMessage: false, error: errorMessage });

                    // English-only validation error is handled by MessageInput's local hint
                    // Clear store error for this case to avoid showing duplicate/stale message
                    if (error.response?.data?.code === 'ENGLISH_ONLY') {
                        set({ error: null });
                    }

                    return null;
                }
            },

            startConversation: async (participantId: string, initialMessage?: string) => {
                set({ loading: true, error: null });
                try {
                    const response = await apiClient.post<{ conversation: Conversation; isNew: boolean }>(
                        `/conversations`,
                        { participantId, initialMessage }
                    );

                    const { conversation, isNew } = response.data;

                    if (isNew) {
                        set((state) => ({
                            conversations: [conversation, ...state.conversations],
                        }));
                    }

                    set({ currentConversation: conversation, loading: false });
                    return conversation;
                } catch (error: any) {
                    set({
                        loading: false,
                        error: error.response?.data?.message || 'Failed to start conversation',
                    });
                    return null;
                }
            },

            markAsRead: async (conversationId: string) => {
                try {
                    await apiClient.patch(`/conversations/${conversationId}/read`);

                    // Update local state
                    set((state) => ({
                        conversations: state.conversations.map((c) =>
                            c.id === conversationId ? { ...c, unreadCount: 0 } : c
                        ),
                    }));

                    // Notify via WebSocket
                    const { socket } = get();
                    socket?.emit('markAsRead', { conversationId });
                } catch (error: any) {
                    console.error('Failed to mark as read:', error);
                }
            },

            fetchUnreadCount: async () => {
                try {
                    const response = await apiClient.get<{ count: number }>(
                        `/messages/unread-count`
                    );
                    set({ unreadCount: response.data.count });
                } catch (error) {
                    console.error('Failed to fetch unread count:', error);
                }
            },

            setCurrentConversation: (conversation: Conversation | null) => {
                const { currentConversation: existing } = get();
                // Only clear messages if switching to a different conversation
                if (existing?.id !== conversation?.id) {
                    // Drop any stale typing indicators for the conversation
                    // we're opening — the bot may have emitted `typing` while
                    // we were elsewhere and the `typing_stopped` was sent to a
                    // room we had already left.
                    if (conversation) {
                        clearAllTypingForConversation(conversation.id);
                        set((state) => {
                            const newTypingUsers = new Map(state.typingUsers);
                            newTypingUsers.set(conversation.id, []);
                            return {
                                currentConversation: conversation,
                                messages: [],
                                typingUsers: newTypingUsers,
                            };
                        });
                    } else {
                        set({ currentConversation: null, messages: [] });
                    }
                } else {
                    set({ currentConversation: conversation });
                }
            },

            startTyping: (conversationId: string) => {
                const { socket } = get();
                socket?.emit('typing', { conversationId });
            },

            stopTyping: (conversationId: string) => {
                const { socket } = get();
                socket?.emit('stopTyping', { conversationId });
            },

            joinConversation: (conversationId: string) => {
                const { socket } = get();
                socket?.emit('joinConversation', { conversationId });
            },

            leaveConversation: (conversationId: string) => {
                const { socket } = get();
                socket?.emit('leaveConversation', { conversationId });
                // Clear local typing state for this conversation — any future
                // `typing_stopped` event will be missed because we just left
                // the room.
                clearAllTypingForConversation(conversationId);
                set((state) => {
                    const newTypingUsers = new Map(state.typingUsers);
                    newTypingUsers.set(conversationId, []);
                    return { typingUsers: newTypingUsers };
                });
            },

            clearHighlights: () => {
                set({ learningHighlights: [] });
            },

            addLocalMessage: (message: Message) => {
                set((state) => {
                    if (state.messages.some((m) => m.id === message.id)) return state;
                    return { messages: [...state.messages, message] };
                });
                set((state) => ({
                    conversations: state.conversations.map((c) =>
                        c.id === message.conversationId
                            ? { ...c, lastMessage: message, lastMessageAt: message.createdAt }
                            : c,
                    ),
                }));
            },

            updateLocalMessage: (currentId: string, patch: Partial<Message>) => {
                set((state) => ({
                    messages: state.messages.map((m) =>
                        m.id === currentId ? { ...m, ...patch } : m,
                    ),
                }));
            },

            removeLocalMessage: (id: string) => {
                set((state) => ({
                    messages: state.messages.filter((m) => m.id !== id),
                }));
            },
        }),
        { name: 'MessagingStore' }
    )
);

// Utility hooks
export const useConversations = () => useMessagingStore((state) => state.conversations);
export const useCurrentConversation = () => useMessagingStore((state) => state.currentConversation);
export const useMessages = () => useMessagingStore((state) => state.messages);
export const useMessageUnreadCount = () => useMessagingStore((state) => state.unreadCount);
export const useLearningHighlights = () => useMessagingStore((state) => state.learningHighlights);
export const useChatStats = () => useMessagingStore((state) => state.chatStats);
export const useMessagingActions = () => {
    const {
        connect,
        disconnect,
        fetchConversations,
        fetchConversation,
        fetchMessages,
        sendMessage,
        startConversation,
        markAsRead,
        setCurrentConversation,
        startTyping,
        stopTyping,
        joinConversation,
        leaveConversation,
        clearHighlights,
    } = useMessagingStore();
    return {
        connect,
        disconnect,
        fetchConversations,
        fetchConversation,
        fetchMessages,
        sendMessage,
        startConversation,
        markAsRead,
        setCurrentConversation,
        startTyping,
        stopTyping,
        joinConversation,
        leaveConversation,
        clearHighlights,
    };
};
